import PropTypes from "prop-types";
import { Button, Modal } from "react-bootstrap";
import { confirmable, createConfirmation } from "react-confirm";
import { useTranslation } from "react-i18next";
import { BsFillExclamationTriangleFill } from "react-icons/bs";

const Confirmation = ({
  show,
  proceed,
  dismiss,
  cancel,
  confirmation,
  title,
  okText,
  cancelText,
  okButtonStyle,
  cancelButtonStyle,
  ...options
}: {
  show: any;
  proceed: any;
  dismiss: any;
  cancel: any;
  confirmation: any;
  title: any;
  okText: any;
  cancelText: any;
  okButtonStyle: any;
  cancelButtonStyle: any;
}) => {
  const { t } = useTranslation();

  return (
    <Modal
      contentClassName="tw-bg-white tw-rounded-2xl tw-shadow tw-flex-col tw-justify-start tw-items-center tw-inline-flex"
      show={show}
      onHide={() => proceed(false)}
      backdrop="static"
      centered
      size="sm"
    >
      <Modal.Body className="tw-flex tw-flex-col tw-gap-y-4">
        <div className="tw-self-stretch tw-px-4 tw-pt-5 tw-pb-4 tw-flex-col tw-justify-start tw-items-center tw-gap-3 tw-flex">
          {"description" in options && options.description ? (
            <ErrorImage />
          ) : (
            <div className="tw-w-10 tw-h-10 tw-p-2.5 tw-bg-amber-100 tw-rounded-3xl tw-justify-center tw-items-center tw-inline-flex">
              <div className="tw-w-5 tw-h-5 tw-relative tw-flex-col tw-justify-start tw-items-start tw-flex">
                <BsFillExclamationTriangleFill
                  size={20}
                  className="tw-text-amber-500"
                />
              </div>
            </div>
          )}
          <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-center tw-gap-2 tw-flex">
            <div className="tw-self-stretch tw-text-center tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
              {confirmation}
            </div>
            {"description" in options && (options.description as boolean) && (
              <div className="tw-self-stretch tw-text-center tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                {t("KnowledgeBasesCreateConditionalScenario/text-04")}
              </div>
            )}
          </div>
        </div>
        <div className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-gray-50 tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
          <button
            onClick={() => proceed(false)}
            className="tw-self-stretch tw-px-[17px] tw-py-[9px] tw-bg-primary tw-border-none tw-rounded-[99px] tw-shadow tw-justify-center tw-items-center tw-inline-flex tw-text-white tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal"
          >
            {t("ConfirmTreeDeleteModal/key1")}
          </button>
          <button
            onClick={() => proceed(true)}
            className="tw-self-stretch tw-px-[17px] tw-py-[9px] tw-bg-white tw-rounded-[99px] tw-shadow tw-border tw-border-solid tw-border-gray-300 tw-justify-center tw-items-center tw-inline-flex tw-text-gray-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal"
          >
            {t("ConfirmTreeDeleteModal/key2")}
          </button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

Confirmation.propTypes = {
  /** header title */
  title: PropTypes.string,
  confirmation: PropTypes.string, // arguments of your confirm function
  okText: PropTypes.string,
  cancelText: PropTypes.string,
  okButtonStyle: PropTypes.oneOf([
    "primary",
    "secondary",
    "success",
    "danger",
    "warning",
    "info",
    "light",
    "dark",
    "link"
  ]),
  cancelButtonStyle: PropTypes.oneOf([
    "primary",
    "secondary",
    "success",
    "danger",
    "warning",
    "info",
    "light",
    "dark",
    "link"
  ]),
  show: PropTypes.bool, // from confirmable.
  proceed: PropTypes.func, // from confirmable.
  cancel: PropTypes.func, // from confirmable.
  dismiss: PropTypes.func // from confirmable.
};

Confirmation.defaultProps = {
  title: undefined,
  confirmation: undefined,
  okText: "Save it anyway",
  cancelText: "Back to diagram",
  okButtonStyle: "primary",
  cancelButtonStyle: "secondary",
  show: undefined,
  proceed: undefined,
  cancel: undefined,
  dismiss: undefined
};

const confirmLow = createConfirmation(confirmable(Confirmation));

export const ScenarioDeleteConfirm = (
  message: string,
  options = { description: true }
) => {
  return confirmLow(Object.assign({ confirmation: message }, options) as any);
};

const Alert = ({
  show,
  proceed,
  dismiss,
  cancel,
  confirmation,
  title,
  okText,
  okButtonStyle,
  ...options
}: {
  show: any;
  proceed: any;
  dismiss: any;
  cancel: any;
  confirmation: any;
  title: any;
  okText: any;
  okButtonStyle: any;
}) => {
  const header = title ? (
    <Modal.Header>
      <Modal.Title>{title}</Modal.Title>
    </Modal.Header>
  ) : undefined;
  return (
    <>
      {show && (
        <Modal
          size="sm"
          show={show}
          onHide={() => proceed()}
          keyboard={true}
          backdrop="static"
          centered
        >
          {header}
          <Modal.Body>{confirmation}</Modal.Body>
          <Modal.Footer>
            <Button variant={okButtonStyle} onClick={() => proceed()}>
              {okText}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

Alert.propTypes = {
  /** header title */
  title: PropTypes.string,
  confirmation: PropTypes.string, // arguments of your confirm function
  okText: PropTypes.string,
  okButtonStyle: PropTypes.oneOf([
    "primary",
    "secondary",
    "success",
    "danger",
    "warning",
    "info",
    "light",
    "dark",
    "link"
  ]),
  show: PropTypes.bool, // from confirmable.
  proceed: PropTypes.func, // from confirmable.
  cancel: PropTypes.func, // from confirmable.
  dismiss: PropTypes.func // from confirmable.
};

Alert.defaultProps = {
  title: undefined,
  confirmation: undefined,
  okText: "Save it anyway",
  okButtonStyle: "danger",
  show: undefined,
  proceed: undefined,
  cancel: undefined,
  dismiss: undefined
};

const alertLow = createConfirmation(confirmable(Alert));

export const alert = (message: string, options = {}) => {
  return alertLow(Object.assign({ confirmation: message }, options) as any);
};

const ErrorImage = () => {
  return (
    <img
      src="https://cdn.1cdn.app/application/LOUISEBOT/24040205081340_Image1.svg"
      alt="deleteBlock"
    />
  );
};
